import { Autocomplete, Checkbox, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import React from 'react';
import { MuiAutocompleteWithFocus, MuiEllipseStyle } from '../../../../config/MaterialUIConstants';
import { formatVehicleMakeDisplay, getVehicleMakeCount } from '../../../../utility/helpers/vehicleMakeHelper';

const VehicleMakeSelection = ({ showTypography = true, vehicleMake, selectedMakes, setFormSelectedMakes }) => {
    // Create options with "All" as the first option
    const allOption = { _id: 'all', name: 'All' };
    const options = [allOption, ...(Array.isArray(vehicleMake) && vehicleMake?.length > 0 ? vehicleMake : [])];

    // Check if "All" is selected
    const isAllSelected = selectedMakes?.some((make) => make._id === 'all');

    // Custom display text logic
    const displayText = isAllSelected ? 'All' : formatVehicleMakeDisplay(selectedMakes);
    const makeCount = isAllSelected ? getVehicleMakeCount(selectedMakes) - 1 : getVehicleMakeCount(selectedMakes);

    // Handle clear selection
    const handleClearSelection = (event) => {
        event.stopPropagation();
        setFormSelectedMakes([]);
    };

    // Show clear button only when there are selections
    const showClearButton = selectedMakes && selectedMakes.length > 0;

    return (
        <Stack gap={1}>
            {showTypography && (
                <Typography variant="h6" fontSize={16}>
                    Vehicle Make<span className="asterik">*</span>
                </Typography>
            )}

            <Autocomplete
                multiple
                disableCloseOnSelect
                id="vehicle-make-multiselect"
                options={options}
                value={selectedMakes}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
                onChange={(event, newValue) => {
                    console.log('🚀 ~ newValue:', newValue);

                    // handleClearSelection
                    // Check if "All" was just selected
                    const wasAllSelected = selectedMakes?.some((make) => make._id === 'all');
                    const isAllNowSelected = newValue?.some((make) => make._id === 'all');

                    if (isAllNowSelected && !wasAllSelected) {
                        // "All" was just selected - select all vehicle makes
                        setFormSelectedMakes([allOption, ...(Array.isArray(vehicleMake) && vehicleMake?.length > 0 ? vehicleMake : [])]);
                    } else if (!isAllNowSelected && wasAllSelected) {
                        // "All" was just deselected - deselect all makes
                        setFormSelectedMakes([]);
                    } else if (isAllNowSelected && wasAllSelected) {
                        // "All" is still selected, but other makes were deselected - uncheck "All" as well
                        const newValueWithoutAll = newValue.filter((make) => make._id !== 'all');
                        setFormSelectedMakes(newValueWithoutAll);
                    } else {
                        // Normal selection/deselection
                        setFormSelectedMakes(newValue);
                    }
                }}
                ListboxProps={{ style: { maxHeight: '380px' } }}
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={option?._id || `temp-${Math.random()}`} style={{ minHeight: '44px', paddingRight: 0 }}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option?.name}
                    </li>
                )}
                renderTags={() => null}
                sx={MuiAutocompleteWithFocus}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            placeholder={displayText || 'Vehicle Make'}
                            value={displayText}
                            sx={{
                                ...MuiEllipseStyle,
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(0, 0, 0, 0.87)',
                                    opacity: displayText ? 1 : 0.5,
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        {makeCount > 0 && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    padding: '2px 4px',
                                                    minWidth: '22px',
                                                    height: '22px',
                                                    borderRadius: '100px',
                                                    mr: 0,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    fontSize: '13px',
                                                    background: '#006CDD',
                                                    color: '#fff',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {makeCount}
                                            </Typography>
                                        )}
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                                endAdornment: <>{params.InputProps.endAdornment}</>,
                            }}
                        />
                    );
                }}
            />
        </Stack>
    );
};

export default VehicleMakeSelection;
